import type {
  IFlashCampaign,
  IFlashSale,
  IPromoBanner,
  ITopBanner,
} from '@/sanity/lib/definitions';
import { isCampaignWindowLive } from '@/lib/scheduledCampaign';

/**
 * Flash Sale resolution (see sanity/globals/FlashSale.ts).
 *
 * The campaign singleton is an overlay: while a campaign is live its banner
 * pair wins; outside every window the standard banners stored on the same
 * document are used, falling back to each page own settings. "Rollback" is
 * simply the clock passing the end date — no document is ever mutated.
 *
 * Two kinds of campaign live on the singleton: the flash pair (the top-level
 * fields marketing has always used) and the `campaigns` list of seasonal
 * pairs (Halloween, Black Month, ...), each with its own window. The flash
 * pair wins while live, then the first live entry of the list.
 *
 * The live check is evaluated client-side (hooks/useFlashSaleLive) so open
 * pages flip shortly after a boundary even though the HTML is ISR-cached; a
 * Vercel cron (app/api/flash-sale-cron) purges the cache after each boundary
 * so freshly served HTML (bots, no-JS) catches up too.
 */

/** Key the singleton's own flash pair carries when it is the live campaign. */
export const FLASH_CAMPAIGN_KEY = 'flash';

/**
 * The flash pair is live when the campaign is enabled AND now is inside
 * [startDate, endDate). Both bounds are required — that is what removes the
 * manual midnight publish. Missing or invalid dates mean "not live", never
 * "live forever".
 */
export function isFlashSaleLive(
  config: IFlashSale | null | undefined,
  now: number = Date.now(),
): boolean {
  return isCampaignWindowLive(config, now);
}

/** The singleton's own flash pair in the shape of a list entry. */
export function topLevelCampaign(config: IFlashSale): IFlashCampaign {
  return {
    _key: FLASH_CAMPAIGN_KEY,
    name: config.name,
    isEnabled: config.isEnabled,
    startDate: config.startDate,
    endDate: config.endDate,
    showTopBanner: config.showTopBanner,
    topBanner: config.topBanner,
    showPromoBanner: config.showPromoBanner,
    promoBanner: config.promoBanner,
  };
}

/**
 * The campaign whose banner pair the site shows right now, or null when no
 * window is open. The flash pair first, then the seasonal list in order, so a
 * short flash sale scheduled inside a month-long seasonal window takes over
 * for its days and hands back afterwards. Every entry needs its own Enabled.
 */
export function getLiveFlashCampaign(
  config: IFlashSale | null | undefined,
  now: number = Date.now(),
): IFlashCampaign | null {
  if (!config) return null;
  if (isCampaignWindowLive(config, now)) return topLevelCampaign(config);
  for (const campaign of config.campaigns ?? []) {
    if (isCampaignWindowLive(campaign, now)) return campaign;
  }
  return null;
}

export interface IResolvedFlashBanners {
  showTopBanner: boolean;
  topBanner?: ITopBanner;
  showPromoBanner: boolean;
  promoBanner?: IPromoBanner;
}

/** An override tier only contributes when its toggle is on AND a banner is referenced. */
function pick<T>(enabled: boolean | undefined, banner: T | undefined): T | undefined {
  return enabled ? banner : undefined;
}

/**
 * Resolves which top banner / promo popup a page shows, in priority order:
 *
 *   1. Campaign banner — the live campaign's pair (flash or seasonal)
 *   2. Standard banner — the evergreen pair stored on the Flash Sale doc
 *   3. Page settings   — each page own Show top/promo banner
 *
 * `live` is the live campaign from getLiveFlashCampaign (null when none).
 * `true` still means "the flash pair" for callers that only know the old
 * boolean. Every tier only wins when its toggle is on AND the reference is
 * filled, so an empty field always falls through to the next tier. Nothing
 * is ever written back: when the campaign ends the chain simply resolves one
 * tier lower again.
 */
export function resolveFlashBanners(
  pageSettings:
    | {
        showTopBanner?: boolean;
        topBanner?: ITopBanner;
        showPromoBanner?: boolean;
        promoBanner?: IPromoBanner;
      }
    | null
    | undefined,
  config: IFlashSale | null | undefined,
  live: boolean | IFlashCampaign | null | undefined,
): IResolvedFlashBanners {
  const campaign =
    live === true ? (config ? topLevelCampaign(config) : null) : live || null;

  const flashTop = campaign ? pick(campaign.showTopBanner, campaign.topBanner) : undefined;
  const flashPromo = campaign
    ? pick(campaign.showPromoBanner, campaign.promoBanner)
    : undefined;

  const defaultTop = pick(config?.showDefaultTopBanner, config?.defaultTopBanner);
  const defaultPromo = pick(config?.showDefaultPromoBanner, config?.defaultPromoBanner);

  const pageTop = pick(pageSettings?.showTopBanner, pageSettings?.topBanner);
  const pagePromo = pick(pageSettings?.showPromoBanner, pageSettings?.promoBanner);

  const topBanner = flashTop ?? defaultTop ?? pageTop;
  const promoBanner = flashPromo ?? defaultPromo ?? pagePromo;

  return {
    showTopBanner: !!topBanner,
    topBanner,
    showPromoBanner: !!promoBanner,
    promoBanner,
  };
}
